import styled from "styled-components";
import { useEffect, useRef, useState } from "react";
import { PrimaryButton, SecondaryButton } from "../components/Button";
import { Spacer } from "../editor/EditorHelpers";
import { CertificateContainer, ChallengeButtonBar, ChallengeLearnMore, ChallengeSubheader } from "./ChallengeHelpers";
import { FiDownload, FiLink, FiSettings, FiShare } from "react-icons/fi";
import { useChallenge } from "./useChallenge";
import { Modal, ModalHeader, ModalScrollview, ModalWrapper, Overlay } from "../components/Modal";
import Spinner from "../components/Spinner";
import { KEY_OWN_CID, KEY_TOKEN } from "./challengeModel";
import { useTranslation } from "react-i18next";
import { ChallengeSettings } from "./ChallengeSettings";
import { InfoDialog, InfoDialogProps } from "../components/InfoDialog";
import { RegisterProgress } from "./RegisterProgress";
import { CerfificateFromProgress } from "./CertificateFromProgress";
import { checkedCount, ProgressSection } from "./progressModel";
import { CertificateType } from "./Certificate";



export default function MyChallenge() {
  const { t, i18n } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [scale, setScale] = useState(1000 / 2322);
  const { cid, name, progress, processedProgress, updatedAtEpoch, thumbAtEpoch, censoredEmail, refresh } = useChallenge();
  const [registerProgressOpenWith, setRegisterProgressOpenWith] = useState<ProgressSection | null>(null);
  const [isLearnMoreOpen, setLearnMoreOpen] = useState(false);
  const [isSettingsOpen, setSettingsOpen] = useState(false);
  const [infoDialogProps] = useState<InfoDialogProps>({
    isOpen: false,
    title: "",
    message: "",
  });

  useEffect(() => {
    console.log("progress:", progress);
  }, [progress])

  useEffect(() => {
    function updateScale() {
      if (containerRef.current) {
        const width = containerRef.current.offsetWidth;
        console.log("setScale", width)
        setScale(width / 2322);
      }
    }

    updateScale();
    window.addEventListener('resize', updateScale);
    return () => window.removeEventListener('resize', updateScale);
  }, []);

  // TODO rename
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);
  const [isMenuOpen, setMenuOpen] = useState(false);
  const [isShareReady, setShareReady] = useState(false);
  const [isLinkCopied, setLinkCopied] = useState(false);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target) && 
        buttonRef.current && 
        !buttonRef.current.contains(event.target)
      ) {
        setMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  let getBlobFromChild: (() => Promise<Blob>) | null = null;  

  const uploadImage = async () => {
    if (!getBlobFromChild) return;
    const blob = await getBlobFromChild(); // get blob from child
    // Do something with the blob
    console.log('Got blob of size:', blob ? blob.size : 0);
    const formData = new FormData();
    formData.append('image', blob, 'certificate.jpg');
    formData.append('token', localStorage.getItem(KEY_TOKEN));
    formData.append('own_cid', localStorage.getItem(KEY_OWN_CID));
    if (processedProgress && processedProgress.certificateType !== CertificateType.GREEN) {
      formData.append('rank', processedProgress.certificateType);
    }
    
    fetch('/api/prepare_share.php', {
      method: 'POST',
      body: formData
    })
      .then(response => response.json())
      .then(data => {
        if (data.url) {
          console.log("prepare_share.php:", data.url);
          setShareReady(true);
        } else {
          console.log("prepare_share.php error:", data.error);
        }
      })
      .catch(error => console.error('Error:', error));
  };

  useEffect(() => {
    if (isMenuOpen) {
      const timeSinceThumb = updatedAtEpoch - thumbAtEpoch;
      if (timeSinceThumb > 0) {
        setShareReady(false);
        uploadImage();
      } 
    }
  }, [isMenuOpen]);

  const shareUrlAlone = (processedProgress && processedProgress.certificateType !== CertificateType.GREEN)
    ? `https://hardno.eu/${i18n.language}/${processedProgress.certificateType}/challenge/${cid}`
    : `https://hardno.eu/${i18n.language}/challenge/${cid}`;
  const shareTextAlone = t('challenge.share.buy-european.message');
  //const shareTextWithUrl = shareTextAlone + " " + shareUrlAlone;
  const shareTextWithHashtagAndUrl = shareTextAlone + " #BuyEuropean " + shareUrlAlone;
  //const encodedTextWithUrl = encodeURIComponent(shareTextWithUrl);
  const encodedTextWithHashtagAndUrl = encodeURIComponent(shareTextWithHashtagAndUrl);
  const encodedTextAlone = encodeURIComponent(shareTextAlone);
  const encodedUrlAlone = encodeURIComponent(shareUrlAlone);
  const blueskyShareUrl = `https://bsky.app/intent/compose?text=${encodedTextWithHashtagAndUrl}`;
  const mastodonShareUrl = `https://mastodonshare.com/?text=${encodedTextAlone}&url=${encodedUrlAlone}`;
  const facebookShareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrlAlone}`

  const handleShareBlueSky = () => {
    setMenuOpen(false);
    window.open(blueskyShareUrl, '_blank');
  }

  const handleShareMastodon = () => {
    setMenuOpen(false);
    window.open(mastodonShareUrl, '_blank');
  }

  const handleShareFacebook = () => {
    setMenuOpen(false);
    window.open(facebookShareUrl, '_blank');
  }

  const doCopyLink = async () => {
    console.log("doCopyLink")
    try {
      await navigator.clipboard.writeText(shareUrlAlone);
      setLinkCopied(true);
      setMenuOpen(true);
      setTimeout(() => {
        setMenuOpen(false);
        setLinkCopied(false);
      }, 1500);
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  }

  const handleCopyLink = () => {
    setMenuOpen(false);
    doCopyLink();
  };

  const handleDownload = async () => {
    setMenuOpen(false);
    if (!canvasRef.current) {
      console.log("handleDownload error: no canvasRef")
      return;
    }
    const canvas = canvasRef.current;
    const imgData = canvas.toDataURL("image/jpeg", 0.6);

    const link = document.createElement("a");
    link.href = imgData;
    link.download = `certificate.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCheckClick = (checkKey) => {
    setRegisterProgressOpenWith(checkKey)
  }

  const buttons = <>
    <PrimaryButton onClick={() => setRegisterProgressOpenWith(ProgressSection.UNSPECIFIED)}>{t('challenge.register-progress.button')}</PrimaryButton>
    <SecondaryButton onClick={() => setSettingsOpen(true)}><FiSettings size={18} style={{ verticalAlign: 'middle' }} /></SecondaryButton>
    <ShareWrapper>
      <SecondaryButton style={{height: '100%'}} ref={buttonRef} onClick={() => setMenuOpen(!isMenuOpen)}>
        <FiShare size={18} style={{ verticalAlign: 'middle' }} />
      </SecondaryButton>
      <DropdownMenu ref={dropdownRef} $isOpen={isMenuOpen}>
        {isLinkCopied
          ? (<li>{t('link-copied.info')}</li>)
          : isShareReady
          ? (<>
            <li onClick={handleShareMastodon}>
              <img src="/mastodon_logo_white.svg" alt="Mastodon" style={{height: '18px', verticalAlign: 'middle'}} />
              <Spacer $width="10px"/>
              Mastodon
            </li>
            <li onClick={handleShareBlueSky}>
              <img src="/bluesky_logo72.png" alt="Bluesky" style={{height: '18px', verticalAlign: 'middle'}} />
              <Spacer $width="10px"/>
              Bluesky
            </li>
            <li onClick={handleShareFacebook}>
              <img src="/facebook_logo72.png" alt="Facebook" style={{height: '18px', verticalAlign: 'middle'}} />
              <Spacer $width="10px"/>
              Facebook
            </li>
            <li onClick={handleCopyLink}>
              <FiLink size={18} style={{ verticalAlign: 'middle' }} /><Spacer $width="10px"/>{t('copy-link.button')}
            </li>
            <li onClick={handleDownload}>
              <FiDownload size={18} style={{ verticalAlign: 'middle' }} /><Spacer $width="10px"/>{t('download.button')}
            </li>
          </>)
          : <li><Spinner /></li>
        }
      </DropdownMenu>
    </ShareWrapper>
  </>

  const header = !name || !processedProgress || processedProgress.certificateType !== CertificateType.GOLD
    ? t('challenge.my-buy-european.header')
    : t('challenge.my-buy-european.gold.header', { name })

  const subheader = !name || !processedProgress
    ? t('challenge.my-challenge.unnamed.subheader')
    : checkedCount(processedProgress) === 1
    ? t("challenge.my-challenge.green.task-complete.subheader", { name })
    : processedProgress.certificateType === CertificateType.BRONZE 
    ? t('challenge.my-challenge.bronze.subheader', { name })
    : processedProgress.certificateType === CertificateType.SILVER
    ? t('challenge.my-challenge.silver.subheader', { name })
    : processedProgress.certificateType === CertificateType.GOLD
    ? t('challenge.my-challenge.gold.subheader', { name })
    : t('challenge.my-challenge.green.named.subheader', { name })
  
  const test = [];
  for (let i = 0; i < 40; i++) {
    test.push("test" + i);
  }

  useEffect(() => {
    console.log("registerProgressOpenWith:", registerProgressOpenWith);
  }, [registerProgressOpenWith])
      

  return (
    <>
      <Header>{header}</Header>
      <ChallengeSubheader>{subheader}</ChallengeSubheader>
      <CertificateContainer ref={containerRef}>
        <CerfificateFromProgress
          forwardCanvasRef={canvasRef}
          width={2322}
          height={1220}
          scale={scale}
          registerBlobGetter={(getter) => (getBlobFromChild = getter)}
          name={name}
          processedProgress={processedProgress}
          onCheckClick={handleCheckClick}
        />
      </CertificateContainer>
      {<ChallengeButtonBar>
        <Spacer style={{flexGrow: 1}} />
        <SecondaryButton onClick={() => setLearnMoreOpen(true)}>{t('learn-more.button')}</SecondaryButton>
        {buttons}
        <Spacer style={{flexGrow: 1}} />
      </ChallengeButtonBar>}

      {registerProgressOpenWith && (
        <>
          <Overlay onClick={() => setRegisterProgressOpenWith(null)} />
          <ModalWrapper>
            <Modal>
              <ModalHeader onClose={() => setRegisterProgressOpenWith(null)}>{t('challenge.register-progress')}</ModalHeader>
              <ModalScrollview>
                <RegisterProgress storedProgress={progress} refresh={refresh} openWithSection={registerProgressOpenWith} />
              </ModalScrollview>
            </Modal>
          </ModalWrapper>
        </>
      )}

      {isSettingsOpen && (
        <>
          <Overlay onClick={() => setSettingsOpen(false)} />
          <ModalWrapper>
            <Modal>
              <ModalHeader onClose={() => setSettingsOpen(false)}>{t('challenge.settings')}</ModalHeader>
              <ModalScrollview>
                <ChallengeSettings currentName={name} censoredEmail={censoredEmail} refresh={refresh} />
              </ModalScrollview>
            </Modal>
          </ModalWrapper>
        </>
      )}

      {isLearnMoreOpen && <ChallengeLearnMore onClose={() => setLearnMoreOpen(false)} />}

      <InfoDialog {...infoDialogProps} />

    </>
  )
}

const Header = styled.h1`

`
const ShareWrapper = styled.div`
  position: relative; /* This ensures the share menu is positioned relative to the button */
  display: inline-block;
  //transform: translateY(3px);
  //margin-left: 10px;
`;

const DropdownMenu = styled.ul<{ $isOpen: boolean }>`
  position: absolute;
  bottom: 110%; /* Position it directly above the button */
  right: 0; /* Align it to the right edge of the wrapper */
  margin: 10px 0 0;
  padding: 0;
  list-style: none;
  background-color: var(--bg-color);
  border: 1px solid var(--border-color);
  color: var(--text-color);
  border-radius: 8px;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 100;
  display: ${(props) => (props.$isOpen ? 'block' : 'none')};

  li {
    display: flex; /* This ensures emoji and text are in the same row */
    align-items: center; /* Aligns the items vertically */
    white-space: nowrap; /* Prevent wrapping */
    padding: 10px 20px;
    cursor: pointer;

    &:hover {
      background-color: var(--bg-color-hover);
      border-radius: 8px;
    }
  }
`;

